import { useClassesById } from '../../../selectors/global';
import { TClassSummary, TSpecSummary } from '../../../../../types/utilityTypes';
import { IRioSelectProps, RioSelectSingleOption } from '../../../../components/presentational/select/types';
import { RioSelect } from '../../../../components/presentational/select/Select';
import { DEFAULT_SPEC_VIEW_OPTIONS, formatSpecLabelWithViewConfig, useSpecOptions } from './lib';
import { ISpecOptionProps, SpecOption } from './SpecOption';
import { TSpecSelectViewOptions } from './types';
import { ReactNode } from 'react';
import { ExtendedSpecRole } from '../../../../../types/wowTypes';

export interface ISpecSelectProps<
  Value,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false
> extends Omit<IRioSelectProps<TSpecSummary, Value, IsMulti, IsClearable>, 'options' | 'getOptionValue' | 'onChange'> {
  viewOptions?: TSpecSelectViewOptions;
  optionExtraRender?: (spec: TSpecSummary) => ReactNode;
  getOptionValue?: (spec: TSpecSummary, cls: TClassSummary) => Value;
  onChange: (
    value: IsMulti extends true ? Value[] : IsClearable extends true ? Value | null : Value,
    option: IsClearable extends true ? RioSelectSingleOption<TSpecSummary> | null : RioSelectSingleOption<TSpecSummary>,
    optionClass: IsClearable extends true ? TClassSummary | null : TClassSummary
  ) => void;
  roles?: ExtendedSpecRole[];
  classId?: number;
  optionProps?: Partial<ISpecOptionProps>;
}

export const SpecSelect = <Value, IsMulti extends boolean = false, IsClearable extends boolean = false>({
  viewOptions,
  optionExtraRender,
  getOptionValue,
  roles,
  classId,
  optionProps,
  ...props
}: ISpecSelectProps<Value, IsMulti, IsClearable>) => {
  const normalizedViewOptions = Object.assign({}, DEFAULT_SPEC_VIEW_OPTIONS, viewOptions);
  const classesById = useClassesById();
  const specOptions = useSpecOptions(roles, classId);

  return (
    <RioSelect
      {...props}
      options={specOptions}
      getOptionValue={
        getOptionValue
          ? (spec) => {
              return getOptionValue(spec, classesById[spec.class_id]);
            }
          : undefined
      }
      onChange={(value, option) => {
        const cls = option ? classesById[option.class_id] : null;
        props.onChange(value, option, cls as IsClearable extends true ? TClassSummary | null : TClassSummary);
      }}
      getOptionLabel={(option) => {
        const cls = classesById[option.class_id];
        return formatSpecLabelWithViewConfig(option, cls, normalizedViewOptions.menuValue);
      }}
      formatOptionLabel={(spec, { context }) => {
        const cls = classesById[spec.class_id];

        return (
          <SpecOption
            spec={spec}
            cls={cls}
            viewOptions={normalizedViewOptions}
            optionExtraRender={optionExtraRender}
            context={context}
            {...optionProps}
          />
        );
      }}
    />
  );
};
